/**
 * HeaderMenu — menu pojedynczej kolumny w nagłówku podglądu tabeli (strzałka „v" w nagłówku).
 *
 * Popover Radix w PORTALU (nie `absolute` — kreator ma przewijane kontenery): podpis nagłówka,
 * wyrównanie (trzy przyciski z `aria-pressed` w grupie z podpisem), miejsca po przecinku (TYLKO
 * kolumny dziesiętne) i „Ukryj kolumnę" (dla wymaganych/zablokowanych — notatka z kłódką zamiast
 * przycisku; dla ostatniej widocznej kolumny — notatka, że jej nie da się ukryć). Stan otwarcia
 * trzyma edytor (`open` / `onOpenChange`): naraz otwarte jest najwyżej jedno menu albo panel
 * „Kolumny" — zatrzymane `pointerdown` nie dochodzi do Radix, więc „klik poza menu" nie zamknąłby
 * sąsiedniego popovera samo z siebie.
 *
 * Pola tekstowe działają na lokalnym szkicu i zatwierdzają zmianę przy utracie fokusu, Enterze
 * albo zamknięciu menu (klik poza menu, otwarcie innego menu, początek zmiany szerokości) — nie na
 * każdy wpisany znak (jeden zapis = jedna zmiana układu). Esc ANULUJE szkice (wraca ostatnio
 * zatwierdzona wartość) — tak samo jak Esc w polu nazwy zakładki w kreatorze. Wszystkie oczekujące
 * zmiany z obu pól wychodzą jedną łatką (`onPatch` wołane raz), bo wołający liczy łatkę z układu
 * sprzed zmiany — dwie łatki w jednym takcie nadpisałyby się nawzajem. Puste pole podpisu / miejsc
 * po przecinku usuwa nadpisanie; wpis nieliczbowy w polu miejsc po przecinku (przeglądarka oddaje
 * wtedy pustą wartość, ale `validity.badInput` jest prawdą — czytane w chwili zatwierdzenia, bo
 * React nie woła `onChange` przy zmianie ''→'') nie kasuje ustawienia — wraca ostatnia
 * zatwierdzona wartość.
 *
 * „Ukryj kolumnę" zatwierdza szkice i ukrycie jedną zmianą układu (`onHide` dostaje łatkę ze
 * szkicami), zamyka menu tą samą ścieżką co każde zamknięcie, a fokus oddaje wskazanemu celu
 * (`onHideFocus`) — strzałka ukrytej kolumny znika razem z nagłówkiem. Żeby klik szedł jedną
 * łatką, blur pola jest pomijany, gdy fokus idzie na przycisk (Tab) albo trwa jego wciśnięcie
 * wskaźnikiem (Safari i Firefox na macOS nie dają przyciskowi fokusu, więc sam `relatedTarget` nie
 * wystarcza). Gdy fokus opuści przycisk bez jego użycia, szkice są zatwierdzane (jak przy każdym
 * innym przycisku menu); Esc przy fokusie na przycisku też je zatwierdza zamiast gubić — pole
 * opuszczono wcześniej. Sam komponent niczego nie zapisuje: woła `onPatch` / `onHide`, a edytor
 * składa z tego gotową łatkę `settings`.
 *
 * Klik i wciśnięcie myszy w kontrolkach nie bąbelkują wyżej (zatrzymuje je opakowanie, żeby Radix
 * dalej widział własne zdarzenia) — kreator nie traktuje ich jako zaznaczania elementu formularza.
 */

import {
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  effectiveColumnAlign,
  EMBEDDED_TABLE_COLUMN_DECIMALS_MAX,
  EMBEDDED_TABLE_COLUMN_LABEL_MAX,
  type EmbeddedTableColumnAlign,
  type EmbeddedTableColumnPatch,
  isEmbeddedTableColumnPinned,
  normalizeEmbeddedTableColumnLabel,
  type ResolvedEmbeddedTableColumn,
} from '@saas/shared';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ChevronDown,
  EyeOff,
  Lock,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { useOverlayPortalScopeRef } from '@/hooks/useOverlayPortalScope';

import { keepFocusIfMoved, stopBubble, useLatestRef, useOnClosed } from './interaction-helpers';

export interface HeaderMenuProps {
  col: ResolvedEmbeddedTableColumn;
  /** Czy menu jest otwarte — stan trzyma edytor (naraz otwarty najwyżej jeden popover). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Zmiana nadpisań prezentacji kolumny (`null` w polu = usuń nadpisanie). */
  onPatch: (patch: EmbeddedTableColumnPatch) => void;
  /**
   * „Ukryj kolumnę" — wołane tylko dla kolumn, które wolno ukryć. `pending` to niezatwierdzone
   * szkice pól (pusty obiekt, gdy ich nie ma) — wołający składa je z ukryciem w JEDNĄ zmianę.
   */
  onHide: (pending: EmbeddedTableColumnPatch) => void;
  /** Cel fokusu po „Ukryj kolumnę" (strzałka ukrytej kolumny przestaje istnieć). */
  onHideFocus: () => void;
  /**
   * `false` = kolumna jest ostatnią widoczną: zamiast „Ukryj kolumnę" notatka (tabela bez kolumn
   * nie pokazałaby wierszy). Domyślnie `true`.
   */
  canHide?: boolean;
  /**
   * Baza `data-testid` menu: `<baza>-trigger`, `-label`, `-align-<strona>`, `-decimals`, `-hide`,
   * `-locked` (notatka kolumny wymaganej), `-last-visible` (notatka ostatniej widocznej kolumny).
   */
  testId: string;
}

function decimalsToDraft(decimals: number | undefined): string {
  return decimals === undefined ? '' : String(decimals);
}

export function HeaderMenu({
  col,
  open,
  onOpenChange,
  onPatch,
  onHide,
  onHideFocus,
  canHide = true,
  testId,
}: HeaderMenuProps) {
  const { t } = useTranslation();
  const alignLabelId = useId();
  const name = col.label ?? col.descriptor.label;
  const [labelDraft, setLabelDraft] = useState(col.label ?? '');
  const [decimalsDraft, setDecimalsDraft] = useState(decimalsToDraft(col.decimals));
  // Najnowsze szkice także w refie: zatwierdzenie woła się z efektu (zamknięcie wymuszone z góry)
  // i z handlerów, więc nie może czytać wartości z przestarzałego domknięcia.
  const drafts = useRef({
    label: col.label ?? '',
    decimals: decimalsToDraft(col.decimals),
    // Ostatnio widziany `validity.badInput` pola liczbowego (jego wartość jest wtedy pusta). To
    // tylko zapas na zatwierdzenie PO odmontowaniu pola (zamknięcie wymuszone z góry); gdy pole
    // jest w DOM, zatwierdzenie czyta `validity` na żywo (`decimalsInputRef`).
    badInput: false,
  });
  const decimalsInputRef = useRef<HTMLInputElement>(null);
  // Ostatnio zatwierdzone wartości — blur i zamknięcie menu mogą zajść jedno po drugim,
  // a ta sama zmiana nie powinna wyjść dwa razy; do nich też wraca Esc.
  const committed = useRef<{ label: string; decimals: number | undefined }>({
    label: col.label ?? '',
    decimals: col.decimals,
  });
  const hidingRef = useRef(false);
  const hideButtonRef = useRef<HTMLButtonElement>(null);
  // Trwa wciśnięcie „Ukryj kolumnę" wskaźnikiem (od `pointerdown` do puszczenia/anulowania).
  const hidePressRef = useRef(false);
  const hidePressCleanupRef = useRef<(() => void) | null>(null);
  const onPatchRef = useLatestRef(onPatch);
  // Treść menu jest w portalu: rejestracja w otaczającym oknie (`Overlay`) na zapas, spójnie z
  // panelem „Kolumny". Okno rozstrzyga Esc po miejscu aktywnego uchwytu przeciągania, a menu
  // uchwytów nie ma — dziś rejestracja nie zmienia decyzji okna.
  const portalScopeRef = useOverlayPortalScopeRef<HTMLDivElement>();

  const pinned = isEmbeddedTableColumnPinned(col.descriptor);
  const isDecimal = col.descriptor.kind === 'decimal';
  const currentAlign = effectiveColumnAlign(col);

  const alignOptions: ReadonlyArray<{
    value: EmbeddedTableColumnAlign;
    icon: LucideIcon;
    label: string;
  }> = [
    { value: 'left', icon: AlignLeft, label: t('tenant.embeddedTable.alignLeft') },
    { value: 'center', icon: AlignCenter, label: t('tenant.embeddedTable.alignCenter') },
    { value: 'right', icon: AlignRight, label: t('tenant.embeddedTable.alignRight') },
  ];

  const updateLabelDraft = (value: string): void => {
    drafts.current.label = value;
    setLabelDraft(value);
  };

  const updateDecimalsDraft = (value: string, badInput = false): void => {
    drafts.current.decimals = value;
    drafts.current.badInput = badInput;
    setDecimalsDraft(value);
  };

  /**
   * Zatwierdza oczekujące szkice: normalizuje pola, zapamiętuje je jako zatwierdzone i zwraca
   * łatkę ze zmienionymi wartościami (pusta, gdy nic się nie zmieniło). Nieliczbowy wpis miejsc
   * po przecinku wraca do ostatniej poprawnej wartości.
   */
  const commitDrafts = (): EmbeddedTableColumnPatch => {
    const patch: EmbeddedTableColumnPatch = {};

    const label = normalizeEmbeddedTableColumnLabel(drafts.current.label) ?? '';
    updateLabelDraft(label);
    if (label !== committed.current.label) {
      committed.current.label = label;
      patch.label = label === '' ? null : label;
    }

    if (isDecimal) {
      const raw = drafts.current.decimals.trim();
      // Stan pola czytamy w chwili zatwierdzenia: React nie woła `onChange` przy zmianie ''→''
      // (np. śmieci wpisane, a potem skasowane), więc zapamiętana flaga bywa nieaktualna.
      const input = decimalsInputRef.current;
      const badInput = input !== null ? input.validity.badInput : drafts.current.badInput;
      if (badInput) {
        // Pusta wartość pola to efekt śmieci wpisanych do `type=number`, nie świadome czyszczenie.
        updateDecimalsDraft(decimalsToDraft(committed.current.decimals));
      } else if (raw === '') {
        updateDecimalsDraft('');
        if (committed.current.decimals !== undefined) {
          committed.current.decimals = undefined;
          patch.decimals = null;
        }
      } else {
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) {
          updateDecimalsDraft(decimalsToDraft(committed.current.decimals));
        } else {
          const decimals = Math.min(
            EMBEDDED_TABLE_COLUMN_DECIMALS_MAX,
            Math.max(0, Math.round(parsed)),
          );
          updateDecimalsDraft(String(decimals));
          if (decimals !== committed.current.decimals) {
            committed.current.decimals = decimals;
            patch.decimals = decimals;
          }
        }
      }
    }

    return patch;
  };

  const flushDrafts = (): void => {
    const patch = commitDrafts();
    if (Object.keys(patch).length > 0) onPatchRef.current(patch);
  };

  /** Esc: porzuca niezatwierdzone szkice — wraca ostatnio zatwierdzona wartość, bez zapisu. */
  const cancelDrafts = (): void => {
    updateLabelDraft(committed.current.label);
    updateDecimalsDraft(decimalsToDraft(committed.current.decimals));
  };

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      // Otwarcie: szkice startują od aktualnych wartości kolumny.
      const label = col.label ?? '';
      committed.current = { label, decimals: col.decimals };
      updateLabelDraft(label);
      updateDecimalsDraft(decimalsToDraft(col.decimals));
    } else {
      // Zamknięcie (klik poza menu) nie gubi wpisanego, a niezatwierdzonego tekstu.
      flushDrafts();
    }
    onOpenChange(next);
  };

  // Zamknięcie wymuszone z góry (otwarto inne menu, zaczęto zmianę szerokości) nie przechodzi
  // przez `onOpenChange` Radix — szkice zatwierdzamy tutaj (po Esc są już cofnięte do zatwierdzonych).
  useOnClosed(open, flushDrafts);

  const flushDraftsRef = useLatestRef(flushDrafts);

  /** Kończy śledzenie wciśnięcia „Ukryj kolumnę" (zdejmuje nasłuch puszczenia na `document`). */
  const endHidePress = (): void => {
    hidePressRef.current = false;
    hidePressCleanupRef.current?.();
  };

  // Odmontowanie w trakcie wciśnięcia nie zostawia nasłuchu na `document`.
  useEffect(() => () => hidePressCleanupRef.current?.(), []);

  const handleFieldBlur = (event: ReactFocusEvent<HTMLInputElement>): void => {
    // Fokus idzie na „Ukryj kolumnę” klawiaturą albo trwa jego wciśnięcie wskaźnikiem (w Safari
    // i Firefoksie na macOS przycisk nie dostaje fokusu, więc `relatedTarget` jest wtedy puste):
    // szkice zatwierdzi to samo kliknięcie, razem z ukryciem (jedna łatka zamiast dwóch).
    if (hidePressRef.current) return;
    if (event.relatedTarget !== null && event.relatedTarget === hideButtonRef.current) return;
    flushDrafts();
  };

  const handleHidePointerDown = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (event.button !== 0) return;
    endHidePress();
    hidePressRef.current = true;
    const release = (releaseEvent: Event): void => {
      const wasPressing = hidePressRef.current;
      endHidePress();
      if (!wasPressing) return;
      const target = releaseEvent.target;
      const releasedOnHide =
        releaseEvent.type === 'pointerup' &&
        target instanceof Node &&
        hideButtonRef.current?.contains(target) === true;
      // Puszczenie poza „Ukryj kolumnę” albo anulowanie gestu: kliknięcia nie będzie, a blur pola
      // został pominięty — szkice zatwierdzamy tutaj (przy puszczeniu na przycisku zrobi to klik).
      if (!releasedOnHide) flushDraftsRef.current();
    };
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
    hidePressCleanupRef.current = () => {
      document.removeEventListener('pointerup', release, true);
      document.removeEventListener('pointercancel', release, true);
      hidePressCleanupRef.current = null;
    };
  };

  /** Fokus opuścił „Ukryj kolumnę" bez użycia go: szkice, których blur pola nie zatwierdził. */
  const handleHideBlur = (): void => {
    if (hidePressRef.current) return;
    flushDrafts();
  };

  /**
   * Esc: porzuca szkice pola, w którym trwa edycja. Przy fokusie na „Ukryj kolumnę" pole
   * opuszczono wcześniej (Tabem), więc jego szkic jest już „zatwierdzony przez opuszczenie" —
   * zatwierdzamy go, tak samo jak przy opuszczeniu pola na dowolny inny przycisk menu.
   */
  const handleEscapeKeyDown = (): void => {
    if (document.activeElement === hideButtonRef.current) flushDrafts();
    else cancelDrafts();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    flushDrafts();
  };

  const handleAlignClick = (value: EmbeddedTableColumnAlign): void => {
    // Jawne wyrównanie, kliknięte ponownie, wraca do domyślnego (zdejmuje nadpisanie).
    if (col.align === value) {
      onPatch({ align: null });
      return;
    }
    // To wyrównanie już obowiązuje jako domyślne — zapis niczego by nie zmienił.
    if (col.align === undefined && currentAlign === value) return;
    onPatch({ align: value });
  };

  const handleHide = (): void => {
    // Szkice i ukrycie wychodzą JEDNĄ zmianą układu; zamknięcie idzie wspólną ścieżką
    // (zatwierdzenie szkiców jest wtedy puste — już trafiły do `pending`).
    endHidePress();
    const pending = commitDrafts();
    hidingRef.current = true;
    handleOpenChange(false);
    onHide(pending);
  };

  return (
    <span className="inline-flex shrink-0" onClick={stopBubble} onPointerDown={stopBubble}>
      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <IconButton
            size="sm"
            aria-label={t('tenant.embeddedTable.columnMenuFor', { name })}
            data-testid={`${testId}-trigger`}
          >
            <ChevronDown />
          </IconButton>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            ref={portalScopeRef}
            align="end"
            sideOffset={4}
            aria-label={t('tenant.embeddedTable.columnMenuFor', { name })}
            onEscapeKeyDown={handleEscapeKeyDown}
            onCloseAutoFocus={(event) => {
              if (hidingRef.current) {
                // Ukryta kolumna traci nagłówek (i swoją strzałkę) — fokus idzie w ustalone miejsce.
                hidingRef.current = false;
                event.preventDefault();
                onHideFocus();
                return;
              }
              keepFocusIfMoved(event);
            }}
            data-testid={testId}
            className="z-[10001] flex w-64 flex-col gap-3 rounded-lg border border-border-primary bg-bg-elevated p-3 shadow-lg focus:outline-none"
          >
            <Input
              size="sm"
              label={t('tenant.embeddedTable.labelField')}
              placeholder={col.descriptor.label}
              maxLength={EMBEDDED_TABLE_COLUMN_LABEL_MAX}
              value={labelDraft}
              onChange={(event) => updateLabelDraft(event.target.value)}
              onBlur={handleFieldBlur}
              onKeyDown={handleKeyDown}
              data-testid={`${testId}-label`}
            />

            <div className="flex flex-col gap-1">
              <span id={alignLabelId} className="text-sm font-medium text-text-secondary">
                {t('tenant.embeddedTable.alignLabel')}
              </span>
              <div role="group" aria-labelledby={alignLabelId} className="flex items-center gap-1">
                {alignOptions.map(({ value, icon: Icon, label }) => {
                  const pressed = currentAlign === value;
                  return (
                    <IconButton
                      key={value}
                      size="sm"
                      variant={pressed ? 'subtle' : 'ghost'}
                      aria-label={label}
                      aria-pressed={pressed}
                      onClick={() => handleAlignClick(value)}
                      data-testid={`${testId}-align-${value}`}
                    >
                      <Icon />
                    </IconButton>
                  );
                })}
              </div>
            </div>

            {isDecimal && (
              <Input
                ref={decimalsInputRef}
                size="sm"
                type="number"
                inputMode="numeric"
                min={0}
                max={EMBEDDED_TABLE_COLUMN_DECIMALS_MAX}
                step={1}
                label={t('tenant.embeddedTable.decimalsLabel')}
                value={decimalsDraft}
                onChange={(event) =>
                  updateDecimalsDraft(event.target.value, event.target.validity.badInput)
                }
                // `onChange` Reacta pomija zmianę ''→'' — `input` widzi też śmieci skasowane/dopisane
                // do już pustego pola; zapas flagi na zatwierdzenie po odmontowaniu pola.
                onInput={(event) => {
                  drafts.current.badInput = event.currentTarget.validity.badInput;
                }}
                onBlur={handleFieldBlur}
                onKeyDown={handleKeyDown}
                data-testid={`${testId}-decimals`}
              />
            )}

            {pinned ? (
              <p
                className="flex items-center gap-1.5 text-xs text-text-muted"
                data-testid={`${testId}-locked`}
              >
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('tenant.embeddedTable.requiredNote')}
              </p>
            ) : !canHide ? (
              <p
                className="flex items-center gap-1.5 text-xs text-text-muted"
                data-testid={`${testId}-last-visible`}
              >
                <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('tenant.embeddedTable.lastVisibleNote')}
              </p>
            ) : (
              <Button
                ref={hideButtonRef}
                variant="ghost"
                size="sm"
                className="w-full justify-start font-normal"
                leftIcon={<EyeOff className="h-4 w-4" aria-hidden="true" />}
                onPointerDown={handleHidePointerDown}
                onBlur={handleHideBlur}
                onClick={handleHide}
                data-testid={`${testId}-hide`}
              >
                {t('tenant.embeddedTable.hideColumn')}
              </Button>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </span>
  );
}
